import { ArrowUpRight, Clock, AlertTriangle, Banknote, CheckCircle } from 'lucide-react';
import { formatCurrencyAmount, formatPercentageValue } from '../../../utils/miscFunctions';
import DashboardDatePickers, { SelectedDateRange } from '../../../components/dashboard/date-range-selector/datePicker';
import useUserAccount from '../../../hooks/userAccountHook';
import Loader from '../../../components/Loader';

const SkeletonLoader = () => (
  <div className="space-y-4">
    <div className="h-6 bg-gray-200 dark:bg-darkBg-border rounded w-1/3 animate-pulse"></div>
    <div className="h-10 bg-gray-200 dark:bg-darkBg-border rounded w-2/3 animate-pulse"></div>
    <div className="h-4 bg-gray-200 dark:bg-darkBg-border rounded w-1/2 animate-pulse"></div>
  </div>
);

interface IFinancingOverviewMetrics {
  data: {
    pendingApplications: number,
    totalFinancingAmount: number,
    approvedFinancingAmount: number,
    rejectedApplications: number,
    approvedApplications: number,
    statusDistribution: any,
    approvalRate: number,
    totalApplications: number,
    inApprovalFinancingAmount: number,
    inApprovalApplications: number,
    inReviewApplications: number,
    inReviewFinancingAmount: number,
    isMetricLoading: boolean,
  }
  setSelectedDateRange: (data: SelectedDateRange) => void
}


const FinancingOverviewPanel = ({ data, setSelectedDateRange }: IFinancingOverviewMetrics) => {
  // Show skeleton loader while loading
  if (data?.isMetricLoading) {
    return (
      <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border  p-6">
        <div className="h-8 bg-gray-200 dark:bg-darkBg-border rounded w-1/4 mb-6 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 my-6">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="bg-white dark:bg-darkBg rounded-lg p-4 shadow border-l-4 border-gray-200 dark:border-darkBg-border">
              <SkeletonLoader />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const {user} = useUserAccount();
  // Calculate percentages safely (avoid division by zero)
  const totalAmount = data?.totalFinancingAmount || 1;
  const totalApps = data?.totalApplications || 0;

  // Calculate raw counts
  const approvedCount = data?.approvedApplications || 0;
  const pendingCount = data?.pendingApplications || 0;
  const rejectedCount = data?.rejectedApplications || 0;
  const inApprovalCount = data?.inApprovalApplications || 0;
  const inReviewCount = data?.inReviewApplications || 0;

  // Calculate percentages ensuring they add up to 100%
  const approvedPercentage = totalApps > 0 ? (approvedCount / totalApps) * 100 : 0;
  const pendingPercentage = totalApps > 0 ? (pendingCount / totalApps) * 100 : 0;

  // For rejected, calculate what's left to ensure total is 100%
  const remainingPercentage = 100 - (approvedPercentage + pendingPercentage);
  const rejectedPercentage = Math.max(0, Math.min(100, remainingPercentage));

  // Calculate amount percentage for the approved financing amount
  const paidAmountPercentage = (data?.approvedFinancingAmount / totalAmount) * 100;

  // Calculate the actual counts
  const getStatistics = ()=>{

    let statistics = [
      {
        title: 'Total Applications',
        count: totalApps,
        amount: data?.totalFinancingAmount,
        borderColor: 'border-blue-500 dark:border-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        textColor: 'text-blue-600 dark:text-blue-300',
        icon: <Banknote className="h-6 w-6 text-blue-600 dark:text-blue-300" />,
        percentage: 100,
        isMetricLoading: data?.isMetricLoading,
      }, 
      {
        title: 'Approved',
        count: approvedCount,
        amount: data?.approvedFinancingAmount,
        borderColor: 'border-green-500 dark:border-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        textColor: 'text-green-600 dark:text-green-300',
        icon: <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-300" />,
        percentage: approvedPercentage,
        isMetricLoading: data?.isMetricLoading,
      },
    ];

    if(!user.isBroker()){
      statistics.push(
        {
          title: 'Pending',
          count: pendingCount,
          amount: data?.pendingApplications,
          borderColor: 'border-yellow-500 dark:border-yellow-400',
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
          textColor: 'text-yellow-600 dark:text-yellow-300',
          icon: <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />,
          percentage: pendingPercentage,
          isMetricLoading: data?.isMetricLoading,
        },
        {
          title: 'Rejected',
          count: rejectedCount,
          amount: data?.rejectedApplications,
          borderColor: 'border-red-500 dark:border-red-400',
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          textColor: 'text-red-600 dark:text-red-300',
          icon: <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-300" />,
          percentage: rejectedPercentage,
          isMetricLoading: data?.isMetricLoading,
        }
      )
    }

    if(user.isBroker()){
      statistics.push(
        {
          title: 'In Approval',
          count: inApprovalCount,
          amount: data?.inApprovalFinancingAmount,
          borderColor: 'border-orange-500 dark:border-orange-400',
          bgColor: 'bg-orange-100 dark:bg-orange-900/30',
          textColor: 'text-orange-600 dark:text-orange-300',
          icon: <AlertTriangle className="h-6 w-6 text-orange-600 dark:text-orange-300" />,
          percentage: rejectedPercentage,
          isMetricLoading: data?.isMetricLoading,
        },
            {
          title: 'In Review',
          count: inReviewCount,
          amount: data?.inReviewFinancingAmount,
          borderColor: 'border-yellow-500 dark:border-yellow-400',
          bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
          textColor: 'text-yellow-600 dark:text-yellow-300',
          icon: <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />,
          percentage: pendingPercentage,
          isMetricLoading: data?.isMetricLoading,
        },
      )
    }

      return statistics;
  }

  return (
    <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">Overview</h2>
        <DashboardDatePickers
          onSubmit={(data: SelectedDateRange) => {
            setSelectedDateRange(data);
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 my-6">
        {getStatistics().map((statistic) => (
          <div key={statistic.title} className={`bg-white dark:bg-darkBg rounded-lg p-4 shadow border-l-4 dark:border-t dark:border-b dark:border-r dark:border-t-darkBg-border dark:border-b-darkBg-border dark:border-r-darkBg-border ${statistic.borderColor}`}>
            <div className="flex flex-wrap flex-col-reverse items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">{statistic.title}</p>
                {statistic.isMetricLoading ? <div key={statistic.title} className='flex w-full items-start'><Loader size='xs' classes='!text-left'/></div> : 
                <>
                <p className="text-2xl font-bold text-gray-800 dark:text-darkBg-mainText">
                  {formatCurrencyAmount(statistic.amount)}
                </p>
                <div className="text-xs text-gray-500 dark:text-darkBg-subText mt-1">
                  {statistic.count} {statistic.count < 2 ? 'application' : 'applications'}
                  {statistic.title !== 'Total Applications' && statistic.count > 0 && (
                    ` • ${formatPercentageValue(statistic.percentage)}% of total`
                  )}
                </div>
                </>}
              </div>
              <div className={`rounded-full ${statistic.bgColor} p-3`}>
                {statistic.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm font-medium text-gray-700 dark:text-darkBg-subText">
          <span className="text-green-600 dark:text-green-400">{formatPercentageValue(approvedPercentage)}%</span> of applications approved
        </div>
      </div>
    </div>
  );
};

export default FinancingOverviewPanel;
